// @ts-nocheck
import { createContext } from 'react';

export const PrivacySettingsContext = createContext({
  consentMode: 'disabled',
  doNotSellConsentEnabled: false,
  enableGpc: false,
  gpcSignal: false,
  privacySettingsSet: false,
  privacySettingsState: [],
  privacySettingCategories: [] as ConsentCategoryItem[],
  currentConsentState: {} as { [key: string]: string },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updatePrivacySettings: (params: {
    acceptAll?: boolean;
    doNotSell?: boolean;
    gpcSignal?: boolean;
    consentState?: object | undefined;
  }) => {},
});

function getAcceptState(
  currentState: string,
  acceptAll: boolean,
  subcategory: string,
  doNotSell: boolean,
) {
  if (subcategory === 'strictly_necessary') {
    return 'accepted';
  }
  if (subcategory === 'marketing' && doNotSell) {
    return 'declined';
  }
  if (subcategory === 'do_not_sell' && doNotSell) {
    return 'accepted';
  }
  if (currentState) {
    return currentState;
  }
  if (['do_not_sell', 'limit_pii_use'].includes(subcategory)) {
    return 'declined';
  }

  if (acceptAll) {
    return 'accepted';
  }
  return 'declined';
}
const displayOrder = {
  strictly_necessary: 30,
  limit_pii_use: 60,
  do_not_sell: 70,
};

function getDisplayOrder(subcategory) {
  const value = displayOrder[subcategory];
  if (value === undefined) {
    return 50;
  }
  return value;
}

function getUpdatePrivacySettings(inCategories) {
  const categories = [...inCategories];
  return ({
    acceptAll,
    doNotSell,
    gpcSignal,
    consentState = {},
  }: {
    acceptAll: boolean;
    doNotSell: boolean;
    gpcSignal: boolean;
    consentState: object | undefined;
  }) => {
    const consents = categories.reduce(
      (acc, { category, subcategory, label }) => {
        acc[subcategory] = {
          category,
          subcategory,
          label,
          consentState: getAcceptState(
            consentState[subcategory],
            acceptAll,
            subcategory,
            doNotSell,
          ),
          consentAction: 'user_click',
        };
        return acc;
      },
      {},
    );

    if (doNotSell) {
      if (consents.do_not_sell && consents.marketing) {
        consents.do_not_sell.consentState = 'accepted';
        consents.marketing.consentState = 'declined';
      }
      if (gpcSignal) {
        consents.global_privacy_control = {
          category: 'privacy_setting',
          subcategory: 'global_privacy_control',
          label: 'detected',
          consentState: 'accepted',
          consentAction: 'user_click',
        };
      }
    }
    return window.concord.consent.addConsentEvents(Object.values(consents));
  };
}

export function usePrivacySettingsContext(
  configContext,
  { events },
  { types },
) {
  const { consentMode, doNotSellConsentEnabled, enableGpc, limitPii } =
    configContext;

  const privacySettingsState = events.filter(
    ({ category, label }: { category: string; label: string }) =>
      category === 'privacy_setting' && label === 'consent',
  );
  const getConsentState = (inSubcategory) =>
    privacySettingsState.find(
      ({ subcategory }) => inSubcategory === subcategory,
    )?.consentState;
  const privacySettingCategories = types
    .filter(
      ({ category, subcategory, label }) =>
        category === 'privacy_setting' &&
        label === 'consent' &&
        (!['do_not_sell'].includes(subcategory) || doNotSellConsentEnabled) &&
        (!['limit_pii_use'].includes(subcategory) || limitPii),
    )
    .sort(({ subcategory: a }, { subcategory: b }) => a.localeCompare(b))
    .map((type) => {
      const state = events.find(
        ({ subcategory }) => type.subcategory === subcategory,
      );
      return {
        ...type,
        state,
        displayOrder: getDisplayOrder(type.subcategory),
      };
    })
    .sort(({ displayOrder: a }, { displayOrder: b }) => a - b);

  const privacySettingsSet = privacySettingsState.length !== 0;

  const gpcSignal = enableGpc && (navigator.globalPrivacyControl ?? false);

  const currentConsentState = Object.fromEntries(
    privacySettingCategories.map(({ subcategory }) => [
      subcategory,
      getConsentState(subcategory) ||
        getAcceptState(undefined, false, subcategory, gpcSignal),
    ]),
  );

  return {
    consentMode,
    doNotSellConsentEnabled,
    enableGpc,
    gpcSignal,
    privacySettingsSet,
    privacySettingsState,
    privacySettingCategories,
    currentConsentState,
    updatePrivacySettings: getUpdatePrivacySettings(privacySettingCategories),
  };
}
